import React, { FC, memo } from "react";
import { IconButton } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { ClassifyButton } from "./ClassifyButton";
import { NumberClassifierInput, ClassifiedInput, Tooltip, Units } from "./styled";

import { useSymbolSettingsContext } from "../../../../modules/Style/context/styleSettingsContext";
import { getParameterValue, isParameterByAttribute } from "../../../../evergis/core/classification/getFromClassified";
import { useClassificationEdit } from "../../../Classification/hooks/useClassificationEdit";
import { useNumberClassifierUpdate } from "../../../Classification/hooks/useNumberClassifierUpdate";
import { getSymbolParameterInfo } from "../../../Classification/getSymbolParameterInfo";

import { ClassificationParameter } from "../../../../interfaces/classification";
import { ClassifyAble, ParameterByAttributeValue } from "../../../../interfaces/style";

interface NumberClassifierProps {
  name: ClassificationParameter;
  value: ClassifyAble<number>;
  width?: string;
  disabled?: boolean;
  valueIndex?: number;
  classifyAble?: boolean;
  onChange: (value: number) => void;
}

export const NumberClassifier: FC<NumberClassifierProps> = memo(
  ({ name, value, width, disabled, valueIndex, classifyAble: classifyAbleProp = true, onChange }) => {
    const { t } = useTranslation("common");
    const { ableClassify, inputWidth } = useSymbolSettingsContext();
    const { min, max, step, units, serialize, classifyAble } = getSymbolParameterInfo(name);
    const parameterValue = serialize(getParameterValue(value));
    const editClassification = useClassificationEdit(name, value, valueIndex);

    const { removeClassification, handleChange, handleBlur } = useNumberClassifierUpdate({
      onChange,
      parameter: name,
      parameterValue,
    });

    return isParameterByAttribute(value) ? (
      <Tooltip content={t("symbolEditor.editClassifier")}>
        {ref1 => (
          <ClassifiedInput
            readOnly
            value={`${serialize(getFrom(value.values))} - ${serialize(getTo(value.values))}`}
            innerRef={ref1}
            width={width || "9.125rem"}
            disabled={disabled}
            onClick={editClassification}
            style={{ cursor: "pointer" }}
          >
            <Tooltip content={t("symbolEditor.removeClassifier")}>
              {ref2 => <IconButton innerRef={ref2} kind={"close"} onClick={removeClassification} />}
            </Tooltip>
            <Units>{units}</Units>
          </ClassifiedInput>
        )}
      </Tooltip>
    ) : (
      <NumberClassifierInput
        value={parameterValue}
        width={width || inputWidth}
        onChange={handleChange}
        onBlur={handleBlur}
        step={step}
        min={min}
        max={max}
        units={units}
        title={`${parameterValue}`}
        disabled={disabled}
      >
        {classifyAble && ableClassify && classifyAbleProp ? (
          <ClassifyButton name={name} valueIndex={valueIndex} mini />
        ) : null}
      </NumberClassifierInput>
    );
  },
);

function getFrom(values: ParameterByAttributeValue<number>[]): number {
  return values[0].value;
}

function getTo(values: ParameterByAttributeValue<number>[]): number {
  return values[values.length - 1].value;
}
